import {
	ChevronLeft,
	Home,
	LogOut,
	Mail,
	Trash2,
	User as UserIcon,
} from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { api } from '@api'
import { useAuth } from '@auth/AuthContext.tsx'
import { Header } from '@components/Header.tsx'
import { InviteCode } from '@components/InviteCode.tsx'
import { SettingsRow } from '@components/SettingsRow.tsx'
import { useFavicon } from '@hooks/useFavicon.ts'
import { useOnline } from '@hooks/useOnline.ts'
import { useResource } from '@hooks/useResource.ts'
import { useRouter } from '@router'
import { colorFor } from '@utils/colors.ts'
import { initialFor, memberLabel } from '@utils/members.ts'
import { ChangeEmailDialog } from './ChangeEmailDialog.tsx'
import { DeleteAccountDialog } from './DeleteAccountDialog.tsx'
import { ExportSection } from './ExportSection.tsx'
import styles from './Settings.module.css'

type EditableRowProps = {
	icon: ReactNode
	label: string
	value: string
	inputLabel: string
	maxLength: number
	onSave: (next: string) => Promise<void>
	hint?: ReactNode
}

// A SettingsRow whose value swaps for an inline input once you press Edit.
// Explicit Edit / Save rather than click-the-text-to-edit, so the row says
// what it does. Saving awaits the server before committing — the value is
// one field you just typed, so a spinner beats an optimistic flash and a
// rollback.
const EditableRow = ({
	icon,
	label,
	value,
	inputLabel,
	maxLength,
	onSave,
	hint,
}: EditableRowProps) => {
	const online = useOnline()
	const [editing, setEditing] = useState(false)
	const [draft, setDraft] = useState(value)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (editing) inputRef.current?.focus()
	}, [editing])

	const trimmed = draft.trim()

	const start = () => {
		setDraft(value)
		setError(null)
		setEditing(true)
	}

	const cancel = () => {
		setEditing(false)
		setError(null)
	}

	const save = async () => {
		// Empty is rejected rather than saved: App.tsx reads a null display
		// name as the onboarding sentinel, so a blank one bounces you back
		// into the onboarding form.
		if (trimmed.length === 0) return
		if (trimmed === value) {
			cancel()
			return
		}
		setBusy(true)
		setError(null)
		try {
			await onSave(trimmed)
			setEditing(false)
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Could not save')
		} finally {
			setBusy(false)
		}
	}

	if (!editing) {
		return (
			<SettingsRow
				icon={icon}
				label={label}
				value={value}
				action={
					<button type="button" onClick={start} disabled={!online}>
						Edit
					</button>
				}
				hint={hint}
			/>
		)
	}

	return (
		<SettingsRow
			icon={icon}
			label={label}
			value={
				<input
					ref={inputRef}
					className={styles.inlineInput}
					value={draft}
					maxLength={maxLength}
					aria-label={inputLabel}
					disabled={busy}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault()
							void save()
						}
						if (e.key === 'Escape') cancel()
					}}
				/>
			}
			action={
				<>
					<button type="button" onClick={cancel} disabled={busy}>
						Cancel
					</button>
					<button
						type="button"
						onClick={() => void save()}
						disabled={busy || trimmed.length === 0 || !online}
					>
						{busy ? 'Saving…' : 'Save'}
					</button>
				</>
			}
			hint={error ? <span className={styles.error}>{error}</span> : hint}
		/>
	)
}

export const Settings = () => {
	useFavicon('hewey')
	const { navigate } = useRouter()
	const { state, patchSession, endSession, logout } = useAuth()
	const online = useOnline()
	const [emailDialogOpen, setEmailDialogOpen] = useState(false)
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
	const members = useResource(['household-members'], () =>
		api.household.members(),
	)

	if (state.status !== 'authed') return null
	const { user, household } = state
	const memberCount = members.data?.members.length ?? null

	const saveDisplayName = async (displayName: string) => {
		const res = await api.account.update({ displayName })
		patchSession({ user: res.user })
	}

	const saveHouseholdName = async (name: string) => {
		const res = await api.household.rename({ name })
		patchSession({ household: res.household })
		// The members list shows names, not the household name — but a rename
		// changes the InviteCode subtext, which reads from session state.
	}

	return (
		<main className={styles.screen}>
			<Header
				left={
					<button
						type="button"
						className={styles.back}
						onClick={() => navigate('/')}
						aria-label="Back to home"
					>
						<ChevronLeft size={22} aria-hidden />
					</button>
				}
				center={<h1 className={styles.title}>Settings</h1>}
			/>

			<section className={styles.section}>
				<h2 className={styles.sectionTitle}>You</h2>
				<EditableRow
					icon={<UserIcon size={18} />}
					label="Display name"
					value={user.displayName ?? ''}
					inputLabel="Your display name"
					maxLength={100}
					onSave={saveDisplayName}
					hint="How your family sees you on shared lists."
				/>
				<SettingsRow
					icon={<Mail size={18} />}
					label="Email"
					value={user.email}
					action={
						<button
							type="button"
							onClick={() => setEmailDialogOpen(true)}
							disabled={!online}
						>
							Change
						</button>
					}
					hint="Where your sign-in codes are sent."
				/>
			</section>

			<section className={styles.section}>
				<h2 className={styles.sectionTitle}>Household</h2>
				<EditableRow
					icon={<Home size={18} />}
					label="Household name"
					value={household.name ?? ''}
					inputLabel="Household name"
					maxLength={100}
					onSave={saveHouseholdName}
				/>
				<InviteCode
					householdName={household.name}
					code={household.joinCode}
				/>

				<h3 className={styles.subTitle}>Members</h3>
				{members.data === undefined ? (
					<p className={styles.muted}>Loading…</p>
				) : (
					<ul className={styles.members}>
						{members.data.members.map((m) => (
							<li key={m.id} className={styles.member}>
								<span
									className={styles.avatar}
									style={{ background: colorFor(m.id) }}
									aria-hidden
								>
									{initialFor(m)}
								</span>
								<span className={styles.memberName}>
									{memberLabel(m)}
								</span>
								{m.id === user.id ? (
									<span className={styles.you}>You</span>
								) : null}
							</li>
						))}
					</ul>
				)}
			</section>

			<ExportSection />

			<section className={styles.section}>
				<button
					type="button"
					className={styles.signOut}
					onClick={() => void logout()}
					// The website's session cookie can only be cleared by the server.
					disabled={!online}
				>
					<LogOut size={16} aria-hidden /> Sign out
				</button>
				{/* Disabled until the members list has loaded: the dialog's
				    wording turns on whether anyone is left behind, and
				    guessing it is the one thing it must not do. */}
				<button
					type="button"
					className={styles.deleteAccount}
					onClick={() => setDeleteDialogOpen(true)}
					disabled={memberCount === null || !online}
				>
					<Trash2 size={16} aria-hidden /> Delete your account
				</button>
			</section>

			<ChangeEmailDialog
				open={emailDialogOpen}
				currentEmail={user.email}
				onClose={() => setEmailDialogOpen(false)}
				onChanged={(next) => patchSession({ user: next })}
			/>

			<DeleteAccountDialog
				open={deleteDialogOpen}
				soleMember={memberCount === 1}
				householdName={household.name}
				onClose={() => setDeleteDialogOpen(false)}
				onDeleted={() => {
					// The session is already gone server-side and the cookie
					// cleared, so this just catches the UI up. Landing renders
					// the sign-in screen for an anonymous visitor.
					endSession()
					navigate('/')
				}}
			/>
		</main>
	)
}
